import { useEffect, useState } from 'react'
import { supabase } from '../../lib/supabase'
import { Deal, User } from '../../types'
import { pipelineSideOf } from '../../lib/dealStatusService'
import { auditService } from '../../services/auditService'
import { ACTIONS, logAction } from '../../services/actionLog'
import { loadCompanyIdentity } from '../../services/companySettings'
import './BuyerDetailsPanel.css'
import { toast } from '../Toast'

/**
 * Who the vehicle changes hands to - and which way round that is depends on the side of
 * the pipeline the deal sits on.
 *
 * On an outflow deal we are selling, and the buyer is a customer somebody has to type in.
 * On an inflow deal we are the buyer, and the details are always the same: they are ours.
 * Asking a salesperson to retype an ABN and a dealer licence number onto a purchase from
 * memory is how a wrong ABN ends up on a contract, so the panel fills them in from
 * settings instead. They stay editable, because a purchase is occasionally written in
 * another entity's name, and a screen that will not let you say so is worse than one
 * that guesses wrong.
 *
 * The company block comes from admin_settings, so a change of address or a renewed
 * licence is a row edit rather than a deploy. The two personal fields - who signed and
 * what number to call - come from the person who is logged in.
 *
 * buyer_name, buyer_email and buyer_mobile were columns on deals that no screen in the
 * platform ever wrote. Everything downstream depended on them: the generated invoice
 * carried a null buyer, markAsSent logged no buyer email and left the invoice at draft,
 * and arService.fetchOpenInvoices filters on sent/overdue/disputed - so the AR ladder,
 * the SMS cadence, aged debtors and the AR contract register all had nothing to work on.
 */

interface Props {
  deal: Deal
  user: User
  onSaved: (msg: string) => void
}

const EMAIL_RE = /^\S+@\S+\.\S+$/
// AU mobile, tolerant of spaces and the +61 form.
const MOBILE_RE = /^(?:61)?0?4\d{8}$/
const ABN_RE = /^\d{11}$/

const clean = (v: string) => v.replace(/\s+/g, ' ').trim()
const digits = (v: string) => v.replace(/\D/g, '')

export default function BuyerDetailsPanel({ deal, user, onSaved }: Props) {
  /* Inflow means we are buying. Everything about how this panel reads and what it
     fills in follows from that one fact. */
  const weAreTheBuyer = pipelineSideOf(deal.deal_status) === 'inflow'

  const [form, setForm] = useState({
    buyer_name: deal.buyer_name || '',
    buyer_company: deal.buyer_company || '',
    buyer_abn: (deal as any).buyer_abn || '',
    buyer_lmct: (deal as any).buyer_lmct || '',
    buyer_authorised_officer: deal.buyer_authorised_officer || '',
    buyer_email: deal.buyer_email || '',
    buyer_mobile: (deal as any).buyer_mobile || '',
    buyer_address: deal.buyer_address || '',
  })

  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

  /* Which boxes we filled in, so the panel can say so plainly rather than leaving
     someone to wonder where the text came from. */
  const [prefilled, setPrefilled] = useState<string[]>([])

  /* Fill our own details in, once, and only into boxes that are empty. Anything the
     deal already carries was somebody's decision and is left exactly as it is. */
  useEffect(() => {
    if (!weAreTheBuyer) return
    let live = true

    const fill = async () => {
      const company = await loadCompanyIdentity()
      if (!live) return

      const ours: Record<string, string> = {
        buyer_name: company ? company.name : '',
        buyer_abn: company ? company.abn : '',
        buyer_lmct: company ? company.lmct : '',
        buyer_address: company ? company.address : '',
        buyer_email: company ? company.email : '',
        buyer_authorised_officer: user.full_name || '',
        buyer_mobile: user.phone || '',
      }

      /* Compared against the deal rather than against form state, because this runs on
         mount - before anyone has typed - and reading the record keeps it predictable. */
      const onRecord: Record<string, string> = {
        buyer_name: deal.buyer_name || '',
        buyer_abn: (deal as any).buyer_abn || '',
        buyer_lmct: (deal as any).buyer_lmct || '',
        buyer_address: deal.buyer_address || '',
        buyer_email: deal.buyer_email || '',
        buyer_authorised_officer: deal.buyer_authorised_officer || '',
        buyer_mobile: (deal as any).buyer_mobile || '',
      }

      const patch: Record<string, string> = {}
      Object.keys(ours).forEach((k) => {
        if (!onRecord[k] && ours[k]) patch[k] = ours[k]
      })

      if (Object.keys(patch).length === 0) return
      setForm((f) => ({ ...f, ...patch } as typeof f))
      setPrefilled(Object.keys(patch))
    }

    void fill()
    return () => {
      live = false
    }
  }, [weAreTheBuyer, deal.id])

  const set = (k: keyof typeof form, v: string) => {
    setForm((f) => ({ ...f, [k]: v }))
    setFieldErrors((e) => ({ ...e, [k]: '' }))
    // Once a box has been touched it is no longer something we filled in for them.
    setPrefilled((p) => p.filter((x) => x !== k))
  }

  const validate = () => {
    const errs: Record<string, string> = {}

    if (!clean(form.buyer_name)) {
      errs.buyer_name = weAreTheBuyer
        ? 'The buying entity is required - this is the name that goes on the purchase contract.'
        : 'A buyer name is required: the invoice is issued to this name.'
    }

    /* Email is what turns a draft invoice into a sent one, so it is effectively
       mandatory rather than nice to have. */
    if (!clean(form.buyer_email)) {
      errs.buyer_email =
        'An email is required: without it the invoice can never be sent and will never enter the AR ladder.'
    } else if (!EMAIL_RE.test(clean(form.buyer_email))) {
      errs.buyer_email = 'That does not look like an email address.'
    }

    if (form.buyer_mobile && !MOBILE_RE.test(digits(form.buyer_mobile))) {
      errs.buyer_mobile = 'Use an Australian mobile, e.g. 0412 345 678.'
    }

    if (form.buyer_abn && !ABN_RE.test(form.buyer_abn.replace(/\s/g, ''))) {
      errs.buyer_abn = 'An ABN is 11 digits.'
    }

    /* On a purchase somebody has to have signed for the company, and a super admin
       following it up later needs a name to ask for. */
    if (weAreTheBuyer && !clean(form.buyer_authorised_officer)) {
      errs.buyer_authorised_officer =
        'Name the person signing for the company - this is who the seller dealt with.'
    }

    setFieldErrors(errs)
    return Object.keys(errs).length === 0
  }

  const handleSave = async () => {
    setError(null)
    if (!validate()) return

    setSaving(true)
    try {
      const payload: Record<string, any> = {
        buyer_name: clean(form.buyer_name),
        buyer_company: clean(form.buyer_company) || null,
        buyer_abn: form.buyer_abn.replace(/\s/g, '') || null,
        buyer_lmct: clean(form.buyer_lmct) || null,
        buyer_authorised_officer: clean(form.buyer_authorised_officer) || null,
        buyer_email: clean(form.buyer_email).toLowerCase(),
        buyer_mobile: digits(form.buyer_mobile) || null,
        buyer_address: clean(form.buyer_address) || null,
        updated_at: new Date().toISOString(),
      }

      /* On a purchase the buyer is a company, and the separate Company box is not shown
         because it would only repeat the name. Downstream - invoices, the Vehicle Purchase Agreement -
         still reads buyer_company, so it is kept in step here rather than left null. */
      if (weAreTheBuyer && !payload.buyer_company) {
        payload.buyer_company = payload.buyer_name
      }

      if (!deal.buyer_name) payload.buyer_captured_at = new Date().toISOString()

      const { error: err } = await supabase.from('deals').update(payload).eq('id', deal.id)
      if (err) throw err

      /* If an invoice already exists for this deal it was generated with a null
         buyer, so bring it into line rather than leaving a stale one behind. */
      const { data: invoices } = await supabase
        .from('invoices')
        .select('id, status')
        .eq('deal_id', deal.id)

      let syncedInvoices = 0
      for (const inv of invoices || []) {
        if (inv.status === 'paid') continue
        const { error: invErr } = await supabase
          .from('invoices')
          .update({
            buyer_name: payload.buyer_name,
            buyer_company: payload.buyer_company,
            buyer_abn: payload.buyer_abn,
            buyer_lmct: payload.buyer_lmct,
            buyer_email: payload.buyer_email,
            buyer_mobile: payload.buyer_mobile,
            buyer_address: payload.buyer_address,
            updated_at: new Date().toISOString(),
          })
          .eq('id', inv.id)
        if (!invErr) syncedInvoices++
      }

      await auditService.log(user, 'buyer_details_saved', {
        details: {
          deal: deal.id,
          buyer: payload.buyer_name,
          officer: payload.buyer_authorised_officer,
          invoices_synced: syncedInvoices,
        },
      })

      /* Also record it where the deal workspace reads from. auditService above writes
         to its own table which nothing on the workspace has ever looked at - so the
         Buyer Details card sat there saying nothing had been done. Two trails is one
         too many and they should be merged, but not in the same change as the fix. */
      await logAction({
        dealId: deal.id,
        action: ACTIONS.BUYER_DETAILS_SAVED,
        actorId: user.id,
        details: {
          buyer: payload.buyer_name,
          officer: payload.buyer_authorised_officer,
          side: weAreTheBuyer ? 'inflow' : 'outflow',
          invoices_synced: syncedInvoices,
        },
      })

      toast.saved()
      onSaved(
        syncedInvoices > 0
          ? 'Buyer saved, and ' +
              syncedInvoices +
              ' existing invoice' +
              (syncedInvoices === 1 ? '' : 's') +
              ' updated to match.'
          : 'Buyer details saved.'
      )
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save the buyer details')
    } finally {
      setSaving(false)
    }
  }

  const field = (
    key: keyof typeof form,
    label: string,
    opts: { placeholder?: string; hint?: string; required?: boolean; type?: string } = {}
  ) => (
    <div className={'buyer-field' + (fieldErrors[key] ? ' has-error' : '')} key={key}>
      <label htmlFor={'buyer-' + key}>
        {label}
        {opts.required ? <span className="req">*</span> : null}
      </label>
      <input
        id={'buyer-' + key}
        type={opts.type || 'text'}
        value={form[key]}
        placeholder={opts.placeholder}
        onChange={(e) => set(key, e.target.value)}
      />
      {fieldErrors[key] ? (
        <small className="buyer-error">{fieldErrors[key]}</small>
      ) : opts.hint ? (
        <small className="buyer-hint">{opts.hint}</small>
      ) : null}
    </div>
  )

  return (
    <div className="buyer-panel">
      <div className="buyer-head">
        <h4>{weAreTheBuyer ? 'Buyer details - this is us' : 'Buyer details'}</h4>
        {weAreTheBuyer ? (
          <p>
            We are the buyer on a purchase, so these are our own details and they are already
            filled in. Change them only if this vehicle is being bought in another entity's
            name. The authorised officer is whoever is signing for the company.
          </p>
        ) : (
          <p>
            Who the vehicle is being sold to. The invoice is issued to this name and sent to this
            address - an invoice with no buyer email stays a draft and never enters the reminder
            ladder.
          </p>
        )}
      </div>

      {prefilled.length > 0 ? (
        <p className="buyer-prefill">
          Filled in from your company settings and your own profile. Nothing is saved until you
          press Save, and you can change any of it first.
        </p>
      ) : null}

      <div className="buyer-grid">
        {field(
          'buyer_name',
          weAreTheBuyer ? 'Buying entity' : 'Buyer name',
          weAreTheBuyer
            ? { required: true, hint: 'The company the vehicle is being bought by.' }
            : { required: true, placeholder: 'Legal or trading name' }
        )}

        {/* On a purchase this would only repeat the entity name, so it is not shown.
            It is still written, from the entity name, when the deal is saved. */}
        {weAreTheBuyer
          ? null
          : field('buyer_company', 'Company', {
              placeholder: 'If different from the buyer name',
            })}

        {field(
          'buyer_authorised_officer',
          'Authorised officer',
          weAreTheBuyer
            ? {
                required: true,
                placeholder: 'Who is signing for the company',
                hint: 'Defaults to you. Change it if someone else signed.',
              }
            : {
                placeholder: 'Who is signing for the buyer',
                hint: 'Only needed when the buyer is a company.',
              }
        )}

        {field('buyer_email', 'Email', {
          required: true,
          type: 'email',
          placeholder: 'accounts@example.com.au',
          hint: weAreTheBuyer ? 'Where the seller sends the paperwork.' : undefined,
        })}

        {field('buyer_mobile', 'Mobile', {
          placeholder: '0412 345 678',
          /* Left blank when the signed-in person has no number on their profile. Saying
             so beats an empty box under a hint that promises it was filled in. */
          hint: !weAreTheBuyer
            ? 'Used for the AR SMS reminders.'
            : user.phone
            ? 'Your mobile. This is the number the seller is given.'
            : 'There is no mobile on your profile, so nothing was filled in. Add one under your account, or type it here.',
        })}

        {field('buyer_abn', 'ABN', {
          placeholder: '11 digits',
          hint: weAreTheBuyer
            ? 'Ours, from settings. It goes on the purchase contract.'
            : 'Appears on the AR contract register.',
        })}

        {field('buyer_lmct', 'LMCT / dealer licence', {
          placeholder: 'If a licensed dealer',
        })}
      </div>

      <div className="buyer-field wide">
        <label htmlFor="buyer-buyer_address">Address</label>
        <textarea
          id="buyer-buyer_address"
          rows={2}
          value={form.buyer_address}
          placeholder="Street, suburb, state, postcode"
          onChange={(e) => set('buyer_address', e.target.value)}
        />
      </div>

      {error ? <div className="buyer-alert">{error}</div> : null}

      <div className="buyer-actions">
        <button className="buyer-save" onClick={handleSave} disabled={saving}>
          {saving
            ? 'Saving...'
            : deal.buyer_name
            ? 'Update buyer details'
            : 'Save buyer details'}
        </button>
        {deal.buyer_name ? (
          <span className="buyer-current">
            {weAreTheBuyer ? 'Currently buying as ' : 'Currently invoicing '}
            <strong>{deal.buyer_name}</strong>
            {deal.buyer_authorised_officer ? ' (' + deal.buyer_authorised_officer + ')' : ''}
            {deal.buyer_email ? ' at ' + deal.buyer_email : ' - no email on file'}
          </span>
        ) : null}
      </div>
    </div>
  )
}
